// 2026-11-02: the report button, top right on every screen (James's idea). A tap takes a picture of the screen first
// (so the sheet isn't in it), then asks for a problem or an idea; the picture is attached unless they take it off.
import { useEffect, useState } from "react";
import { recentErrorIds } from "../monitoring";
import { captureScreen, sendReport, type ReportKind } from "../report";
import { match, useLocation } from "../router";
import { toast } from "../store";
import { Icon, Sheet } from "./bits";

const OPEN_EVENT = "cao:report";
/** The phone top bar's bug icon (Shell) opens the same sheet as the desktop button. */
export function openReport() {
  window.dispatchEvent(new Event(OPEN_EVENT));
}

/** The phone top bar's report icon (the top bar shows on phones; the desktop button is hidden there). */
export function ReportBarButton() {
  return (
    <button type="button" className="icon-btn topbar-report" data-no-shot onClick={openReport} aria-label="Report a problem or send an idea" title="Report a problem or send an idea" data-action="report.open">
      <Icon name="bug" size={20} />
    </button>
  );
}

export function ReportButton() {
  const { path } = useLocation();
  const [open, setOpen] = useState(false);
  const [taking, setTaking] = useState(false);
  const [shot, setShot] = useState<string | null>(null);
  const [kind, setKind] = useState<ReportKind>("problem");
  const [text, setText] = useState("");
  const [keepShot, setKeepShot] = useState(true);
  const [sending, setSending] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const start = async () => {
    if (taking || open) return;
    setTaking(true);
    const s = await captureScreen();
    setTaking(false);
    setShot(s);
    setKeepShot(!!s);
    setFailure(null);
    setOpen(true);
  };
  const close = () => {
    setOpen(false);
    setShot(null);
  };
  useEffect(() => {
    const f = () => void start();
    window.addEventListener(OPEN_EVENT, f);
    return () => window.removeEventListener(OPEN_EVENT, f);
  });
  const route = match(path) as { name: string; id?: string };
  const projectId = (route.name === "project" || route.name === "review") && route.id ? route.id : null;

  return (
    <>
      <button type="button" className="report-fab" data-no-shot onClick={start} disabled={taking} aria-label="Report a problem or send an idea" title="Report a problem or send an idea" data-action="report.open">
        <Icon name="bug" size={18} />
      </button>
      {open && (
        <div data-no-shot>
          <Sheet title={kind === "problem" ? "Report a problem" : "Send an idea"} onClose={close} className="report-sheet">
            <form
              className="sheet-body"
              onSubmit={async (e) => {
                e.preventDefault();
                if (!text.trim()) return setFailure(kind === "problem" ? "Say what went wrong in a few words." : "Say what you'd like in a few words.");
                setSending(true);
                setFailure(null);
                try {
                  const r = await sendReport({ kind, text, path: location.pathname, projectId, sentryEventIds: recentErrorIds(), screenshot: keepShot ? shot : null });
                  toast(r.duplicate ? "Already sent. We have it." : "Sent. Thanks, we read every one.", "good");
                  setText("");
                  close();
                } catch (err) {
                  setFailure((err as Error).message || "Couldn't send it. Try again in a moment.");
                } finally {
                  setSending(false);
                }
              }}
            >
              <div className="report-kinds" role="radiogroup" aria-label="What is it?">
                {(["problem", "feedback"] as const).map((k) => (
                  <button key={k} type="button" role="radio" aria-checked={kind === k} className={`report-kind${kind === k ? " is-on" : ""}`} onClick={() => setKind(k)}>
                    <Icon name={k === "problem" ? "bug" : "sparkle"} size={16} /> {k === "problem" ? "A problem" : "An idea"}
                  </button>
                ))}
              </div>
              <label className="field">
                <span className="field-label">{kind === "problem" ? "What happened? What did you expect?" : "What would make it better?"}</span>
                <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} maxLength={2000} data-autofocus placeholder={kind === "problem" ? "I tapped Make clip and nothing happened…" : "It would help if…"} />
              </label>
              {shot ? (
                <div className="report-shot">
                  <img src={shot} alt="A picture of your screen when you tapped the button" />
                  <label className="report-shot-keep">
                    <input type="checkbox" checked={keepShot} onChange={(e) => setKeepShot(e.target.checked)} /> Attach this picture of the screen
                  </label>
                </div>
              ) : (
                <p className="sheet-note">We couldn't take a picture of this screen; the report still goes with which page you're on.</p>
              )}
              {failure && <p className="report-failure" role="alert">{failure}</p>}
              <button type="submit" className="btn btn-primary btn-block" disabled={sending}>
                {sending ? "Sending" : "Send to the team"}
              </button>
            </form>
          </Sheet>
        </div>
      )}
    </>
  );
}
